/**
 * WCAG helpers for Google Translate (WCAG 3.1.1 language, 2.4.4 link purpose).
 * Google Translate can leave <html lang="en"> while rendering French/Spanish, and
 * can strip hrefs from external links — both fail WAVE in QA (Company FR, Support ES/FR).
 */

const DEFAULT_LANG = "en";

/** Read target language from googtrans cookie or the translate combo box. */
export function getGoogleTranslateTargetLang() {
  const cookieMatch = document.cookie.match(/(?:^|;\s*)googtrans=([^;]+)/);
  if (cookieMatch) {
    const parts = decodeURIComponent(cookieMatch[1]).split("/").filter(Boolean);
    const target = parts[parts.length - 1];
    if (target && target !== DEFAULT_LANG) {
      return target;
    }
    if (parts.length <= 1) {
      return DEFAULT_LANG;
    }
  }

  const select = document.querySelector("select.goog-te-combo");
  if (select?.value) {
    return select.value;
  }

  return DEFAULT_LANG;
}

/** Keep document + main landmark lang in sync with the active translation. */
export function syncDocumentLangFromGoogleTranslate() {
  const lang = getGoogleTranslateTargetLang();
  document.documentElement.lang = lang;
  const main = document.getElementById("main-content");
  if (main) {
    main.setAttribute("lang", lang);
  }
}

/**
 * Restore hrefs Google Translate may strip; remove stray empty anchors HubSpot
 * or Translate inject without destinations (WAVE "Empty link").
 */
export function repairTranslatedLinks(root = document.body) {
  if (!root?.querySelectorAll) return;

  root.querySelectorAll("a").forEach((anchor) => {
    const href = anchor.getAttribute("href");
    const backup = anchor.getAttribute("data-sri-href");
    const isEmptyHref = !href || href === "" || href === "#";

    if (isEmptyHref && backup) {
      anchor.setAttribute("href", backup);
      return;
    }

    if (isEmptyHref && !anchor.textContent?.trim()) {
      anchor.remove();
    }
  });
}

/** Attach listeners once Google Translate UI is ready. */
export function initGoogleTranslateA11y() {
  syncDocumentLangFromGoogleTranslate();
  repairTranslatedLinks();

  const onTranslateChange = () => {
    syncDocumentLangFromGoogleTranslate();
    repairTranslatedLinks();
  };

  const bindCombo = () => {
    const select = document.querySelector("select.goog-te-combo");
    if (select && !select.dataset.sriA11yBound) {
      select.dataset.sriA11yBound = "true";
      select.addEventListener("change", onTranslateChange);
    }
  };

  bindCombo();

  const translateObserver = new MutationObserver(() => {
    bindCombo();
    onTranslateChange();
  });

  const translateRoot = document.getElementById("google_translate_element");
  if (translateRoot) {
    translateObserver.observe(translateRoot, { childList: true, subtree: true });
  }

  // Translated DOM is replaced under body; re-sync lang + links after each pass.
  let repairTimer;
  const bodyObserver = new MutationObserver(() => {
    clearTimeout(repairTimer);
    repairTimer = setTimeout(() => {
      syncDocumentLangFromGoogleTranslate();
      repairTranslatedLinks();
    }, 300);
  });
  bodyObserver.observe(document.body, { childList: true, subtree: true });

  return () => {
    translateObserver.disconnect();
    bodyObserver.disconnect();
    clearTimeout(repairTimer);
    const select = document.querySelector("select.goog-te-combo");
    if (select) {
      select.removeEventListener("change", onTranslateChange);
    }
  };
}
